import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from 'react'
import s from './ui.module.css'

type Variant = 'primary' | 'secondary' | 'ghost' | 'danger'
type Size = 'sm' | 'md' | 'lg'

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant
  size?: Size
  /* Every mutating action needs this — a button that has been pressed and is
     waiting must say so, or the student presses it again. */
  loading?: boolean
  fullWidth?: boolean
  icon?: ReactNode
}

const variants: Record<Variant, string> = {
  primary: s.primary!,
  secondary: s.secondary!,
  ghost: s.ghost!,
  danger: s.danger!,
}

const sizes: Record<Size, string> = { sm: s.sizeSm!, md: '', lg: s.sizeLg! }

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  {
    variant = 'primary',
    size = 'md',
    loading = false,
    fullWidth = false,
    icon,
    disabled,
    children,
    className,
    type = 'button',
    ...rest
  },
  ref,
) {
  const classes = [s.button, variants[variant], sizes[size], fullWidth ? s.fullWidth : '', className]
    .filter(Boolean)
    .join(' ')

  return (
    <button
      ref={ref}
      type={type}
      className={classes}
      disabled={disabled === true || loading}
      aria-busy={loading || undefined}
      {...rest}
    >
      {loading ? <span className={s.spinner} aria-hidden="true" /> : icon}
      {children}
    </button>
  )
})
